import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
  Slider,
  IconButton,
  Chip,
  ListItemText,
  CircularProgress,
  Tooltip,
  Alert,
} from "@mui/material";
import { 
  Close, 
  Refresh, 
  OpenInNew, 
  Remove, 
  ExpandMore, 
  ExpandLess, 
  Cancel,
  HelpOutline,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import folderService from "../../services/folderService";
import resourceSharingService from "../../services/resourceSharingService";
import { HelpCircle } from "lucide-react";
import {
  KnowledgeBaseRecencyFields,
  applyRecencyPatch,
  renderRetrievalTypeOptions,
} from "./KnowledgeBaseRetrievalFields";
import { SHARED_KB_COPY } from "../../constants/sharedKnowledgeCopy";
import styles from "./KnowledgeBaseDialog.module.scss";

// `readOnly` is the view a share recipient gets: the D1 folders of a shared
// agent belong to its owner, so the form is shown but nothing can be saved.
const KnowledgeBaseDialog = ({ open, onClose, onSave, initialData, readOnly = false }) => {
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  // Compatibility matrix for search types and retrieval methods
  const compatibilityMatrix = {
    "Vector Search": {
      "Basic": { compatible: true, description: "Pure semantic similarity search" },
      "Hybrid": { compatible: true, description: "Semantic similarity combined with keyword matching" },
      "MMR": { compatible: true, description: "Semantic similarity with diversity selection" },
      "HyDE": { compatible: true, description: "Hypothetical document embeddings for better retrieval" }
    },
    "Hybrid Search": {
      "Basic": { compatible: true, description: "Combined vector + keyword search" },
      "Hybrid": { compatible: true, description: "Meaning-based and keyword search combined" },
      "MMR": { compatible: true, description: "Hybrid search with diversity selection" },
      "HyDE": { compatible: true, description: "Hybrid search with hypothetical documents" }
    },
    "Keyword Search": {
      "Basic": { compatible: true, description: "Text-based search only" },
      "Hybrid": { compatible: false, description: "No embeddings for meaning-based search" },
      "MMR": { compatible: false, description: "No embeddings for similarity calculations" },
      "HyDE": { compatible: false, description: "No embeddings for hypothetical documents" }
    }
  };

  // Folder ids arrive as numbers from some APIs (shared folders use the share's
  // resource_id) and as strings from others / from ids persisted in the saved
  // agent JSON — never compare them strictly. Match loosely here, then reuse the
  // folder's own id so MUI's strict Select value matching still works.
  const findFolderById = (id) =>
    folders.find((folder) => String(folder.id) === String(id));

  // Helper function to get available folders for a specific knowledge base
  const getAvailableFolders = (currentKbId) => {
    return folders.filter(folder => {
      // Filter out folders that are already selected in other knowledge bases
      const isSelectedInOtherKB = formData.knowledgeBases
        .filter(otherKb => otherKb.id !== currentKbId)
        .some(otherKb =>
          Array.isArray(otherKb.knowledgeBase) &&
          otherKb.knowledgeBase.some((selectedId) => String(selectedId) === String(folder.id))
        );
      return !isSelectedInOtherKB;
    });
  };

  // Helper component for detailed tooltips
  const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
    <Tooltip
      title={
        <Box sx={{ 
          width: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
          p: 1,
          boxSizing: "border-box",
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{ 
              color: "rgba(255,255,255,0.9)",
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: `${maxWidth}px !important`,
          padding: "12px 16px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          boxSizing: "border-box",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  // Check compatibility for a knowledge base
  const checkCompatibility = (kb) => {
    if (!kb.knowledgeBase || !Array.isArray(kb.knowledgeBase) || kb.knowledgeBase.length === 0) {
      return { compatible: true, message: "" };
    }

    // Get search type from the first selected folder
    const firstFolder = findFolderById(kb.knowledgeBase[0]);
    if (!firstFolder) {
      return { compatible: true, message: "" };
    }

    // Map the searchType from the folder to the compatibility matrix keys
    // The searchType is nested in processingOptions based on the data structure
    let searchType = firstFolder.processingOptions?.searchType || firstFolder.searchType || "hybrid";
    
    // Normalize the search type to match the compatibility matrix
    if (searchType.toLowerCase() === "keyword") {
      searchType = "Keyword Search";
    } else if (searchType.toLowerCase() === "vector") {
      searchType = "Vector Search";
    } else if (searchType.toLowerCase() === "hybrid") {
      searchType = "Hybrid Search";
    } else {
      // Default to Hybrid Search if unknown
      searchType = "Hybrid Search";
    }
    
    // Debug logging to help troubleshoot
    console.log('Folder data:', firstFolder);
    console.log('Extracted searchType:', searchType);
    console.log('Retrieval type:', kb.retrievalType);
    
    const retrievalType = kb.retrievalType || "Basic";
    
    const compatibility = compatibilityMatrix[searchType]?.[retrievalType];
    
    if (!compatibility) {
      return { compatible: true, message: "" };
    }

    if (!compatibility.compatible) {
      return {
        compatible: false,
        message: `Incompatible: ${searchType} + ${retrievalType}. ${compatibility.description}`
      };
    }

    return {
      compatible: true,
      message: `Compatible: ${searchType} + ${retrievalType}. ${compatibility.description}`
    };
  };

  const safeInitialData = initialData || {};
  const [formData, setFormData] = useState({
    ragType: safeInitialData.ragType || "RAG",
    knowledgeBases: safeInitialData.knowledgeBases || [],
  });
  useEffect(() => {
    if (open) {
      // Reset form data when dialog opens with new initialData
      const ragType = safeInitialData.ragType || "RAG";
      const knowledgeBases = safeInitialData.knowledgeBases || [];
      
      // If RAG mode and no knowledge bases, create a default one
      if (ragType === "RAG" && knowledgeBases.length === 0) {
        knowledgeBases.push({
          id: Date.now(),
          knowledgeBase: [],
          numberOfChunks: 5,
          retrievalType: "Basic",
          scoreThreshold: 0.0,
        });
      }
      
      setFormData({
        ragType,
        knowledgeBases,
      });

      // Reset errors
      setErrors({
        knowledgeBase: false,
        numberOfChunks: false,
      });
    }
  }, [open, initialData, safeInitialData.ragType, safeInitialData.knowledgeBases]);

  const [errors, setErrors] = useState({
    knowledgeBase: false,
    numberOfChunks: false,
    compatibility: false,
  });

  const [collapsedKBs, setCollapsedKBs] = useState(new Set());
  
  // State for knowledge base folders
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState(null);

  // Load knowledge base folders from API
  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    setFoldersError(null);
    try {
      const [response, sharedResponse] = await Promise.all([
        // Listing own folders needs kb_dim1. A recipient viewing the owner's
        // folders may not have it; the shared list alone carries the names.
        folderService.getFolders().catch((error) => {
          if (readOnly) return [];
          throw error;
        }),
        resourceSharingService.getSharedWithMe('folder_dim1').catch(() => ({ items: [] })),
      ]);
      const shared = Array.isArray(sharedResponse?.items) ? sharedResponse.items.map((item) => ({
        ...(item.resource || {}), id: item.resource_id ?? item.resource?.id,
        owner_user_id: item.owner?.id, __shared: true, __shareOwner: item.owner,
      })) : [];
      const loaded = [...(Array.isArray(response) ? response : []), ...shared];
      if (readOnly) {
        // The agent holds folder ids only. One the owner attached after sharing
        // has no name available to a recipient, so it gets a generic label.
        (initialData?.knowledgeBases || [])
          .flatMap((kb) => (Array.isArray(kb?.knowledgeBase) ? kb.knowledgeBase : []))
          .forEach((id) => {
            if (!loaded.some((folder) => String(folder.id) === String(id))) {
              loaded.push({ id, name: SHARED_KB_COPY.unknownFolder });
            }
          });
      }
      setFolders(loaded);
    } catch (error) {
      console.error('Error loading folders:', error);
      setFoldersError('Failed to load knowledge base folders');
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, [readOnly, initialData]);

  // Load folders when dialog opens
  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open, loadFolders]);

  // Helper function to render folder information
  const renderFolderInfo = (folder) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {folder.name}
        </Typography>
        {folder.__shared && <Chip label={`Shared by ${folder.__shareOwner?.name || folder.__shareOwner?.email || 'teammate'}`} size="small" color="secondary" sx={{ alignSelf: 'flex-start' }} />}
        {folder.description && (
          <Typography variant="caption" color="text.secondary">
            {folder.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          <Chip
            label={`Vector Store: ${folder.vectorStore || 'PGVector'}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 16 }}
          />
          <Chip
            label={`Embedding: ${folder.embeddingModel || 'text-embedding-3-large'}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 16 }}
          />
        </Box>
      </Box>
    );
  };


  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      
      // If switching to RAG mode, keep only the first knowledge base
      if (field === "ragType" && value === "RAG") {
        newData.knowledgeBases = prev.knowledgeBases.length > 0 ? [prev.knowledgeBases[0]] : [];
      }
      // If switching to Agentic RAG mode, keep all knowledge bases as they are
      
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleAddKnowledgeBase = () => {
    const newKB = {
      id: Date.now(),
      knowledgeBase: [],
      numberOfChunks: 5,
      retrievalType: "Basic",
      scoreThreshold: 0.0,
    };
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: [...prev.knowledgeBases, newKB],
    }));
  };

  const handleRemoveKnowledgeBase = (id) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: prev.knowledgeBases.filter((kb) => kb.id !== id),
    }));
  };

  const handleKnowledgeBaseChange = (id, field, value) => {
    setFormData((prev) => {
      const selectedFolder = field === "knowledgeBase" && Array.isArray(value) && value.length
        ? findFolderById(value[0])
        : null;
      const updatedKnowledgeBases = prev.knowledgeBases.map((kb) =>
        kb.id === id ? {
          ...kb,
          [field]: value,
          ...(field === "knowledgeBase" ? { owner_user_id: selectedFolder?.owner_user_id } : {}),
        } : kb
      );

      // If changing knowledge base selection, clear any other knowledge bases that have the same folder selected
      if (field === "knowledgeBase" && Array.isArray(value) && value.length > 0) {
        const selectedFolderId = value[0];
        return {
          ...prev,
          knowledgeBases: updatedKnowledgeBases.map((kb) => {
            if (kb.id !== id && Array.isArray(kb.knowledgeBase) && kb.knowledgeBase.some((v) => String(v) === String(selectedFolderId))) {
              // Clear the selection in other knowledge bases that have the same folder
              return { ...kb, knowledgeBase: [] };
            }
            return kb;
          }),
        };
      }

      return {
        ...prev,
        knowledgeBases: updatedKnowledgeBases,
      };
    });

    // If changing retrieval type, check compatibility immediately
    if (field === "retrievalType") {
      const updatedKb = formData.knowledgeBases.find(kb => kb.id === id);
      if (updatedKb) {
        const compatibility = checkCompatibility({ ...updatedKb, [field]: value });
        if (!compatibility.compatible) {
          setErrors(prev => ({
            ...prev,
            compatibility: true,
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            compatibility: false,
          }));
        }
      }
    }
  };

  const handleRecencyChange = (id, patch) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: prev.knowledgeBases.map((kb) =>
        kb.id === id ? applyRecencyPatch(kb, patch) : kb
      ),
    }));
  };

  const toggleCollapse = (id) => {
    setCollapsedKBs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const validateForm = () => {
    const newErrors = {
      knowledgeBase: formData.knowledgeBases.length === 0 || formData.knowledgeBases.some(kb => {
        if (Array.isArray(kb.knowledgeBase)) {
          return kb.knowledgeBase.length === 0;
        }
        return !kb.knowledgeBase || kb.knowledgeBase.trim() === "";
      }),
      numberOfChunks: formData.knowledgeBases.some(kb => kb.numberOfChunks <= 0),
      compatibility: formData.knowledgeBases.some(kb => {
        const compatibility = checkCompatibility(kb);
        return !compatibility.compatible;
      }),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSave = () => {
    if (readOnly) return;
    if (validateForm()) {
      onSave(formData);
      onClose(formData);
    }
  };

  const handleCancel = () => {
    onClose(formData);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(formData)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(24, 24, 27, 0.4)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              {readOnly ? "Knowledge Base" : "Configure Knowledge Base"}
            </Typography>
            <InfoTooltip
              title="Compatibility Guide"
              content={
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                    <strong>Search Type + Retrieval Type Compatibility:</strong>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                    ✅ Compatible Combinations:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Vector Search + Basic/Hybrid/MMR/HyDE
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Hybrid Search + Basic/Hybrid/MMR/HyDE
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    • Keyword Search + Basic only
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                    ❌ Incompatible:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Keyword Search + Hybrid (no embeddings)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Keyword Search + MMR (no embeddings)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    • Keyword Search + HyDE (no embeddings)
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                    💡 Recommendation:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                    Use Hybrid Search for maximum flexibility - supports all retrieval methods
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" className={styles.headerHelpIcon} aria-label="Compatibility help">
                <HelpCircle size={16} strokeWidth={2} />
              </IconButton>
            </InfoTooltip>
          </Box>
          <IconButton
            onClick={() => onClose(formData)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          {readOnly && (
            <Alert severity="info" sx={{ mb: 2, fontSize: "0.8rem" }}>
              {SHARED_KB_COPY.dim1Locked}
            </Alert>
          )}
          {/* `inert` takes the form out of pointer and keyboard reach alike. */}
          <Box
            className={`${styles.formContainer}${readOnly ? ` ${styles.readOnlyLock}` : ""}`}
            {...(readOnly ? { inert: "" } : {})}
          >
            {/* RAG Type Selection */}
            <Box className={styles.section}>
              <Box className={styles.ragTypeHeader}>
                <RadioGroup
                  value={formData.ragType}
                  onChange={(e) => handleInputChange("ragType", e.target.value)}
                  className={styles.radioGroup}
                >
                  <FormControlLabel
                    value="RAG"
                    control={<Radio size="small" />}
                    label="RAG"
                    className={styles.radioLabel}
                  />
                  <FormControlLabel
                    value="Agentic RAG"
                    control={<Radio size="small" />}
                    label="Personalize Data Source"
                    className={styles.radioLabel}
                  />
                </RadioGroup>
              </Box>
            </Box>

            {/* Knowledge Base Configuration */}
            {formData.ragType === "RAG" ? (
              <Box className={styles.section}>
                <Typography variant="body2" className={styles.sectionLabel}>
                  Knowledge Base <span className={styles.required}>*</span>
                </Typography>
                <Box className={styles.knowledgeBaseContainer}>
                  <FormControl
                    fullWidth
                    size="small"
                    className={styles.selectField}
                    error={errors.knowledgeBase}
                  >
                    <InputLabel>Select Folder...</InputLabel>
                    <Select
                      value={Array.isArray(formData.knowledgeBases[0]?.knowledgeBase) && formData.knowledgeBases[0].knowledgeBase.length > 0 ? findFolderById(formData.knowledgeBases[0].knowledgeBase[0])?.id ?? "" : ""}
                      onChange={(e) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: [], numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "knowledgeBase", [e.target.value]);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, knowledgeBase: [e.target.value] }] }));
                        }
                      }}
                      label="Select Folder..."
                      renderValue={(selected) => {
                        if (!selected) return "";
                        const folder = findFolderById(selected);
                        return folder ? folder.name : "";
                      }}
                      disabled={loadingFolders}
                    >
                      {loadingFolders ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading folders...
                        </MenuItem>
                      ) : foldersError ? (
                        <MenuItem disabled>
                          <Cancel sx={{ mr: 1, color: 'error.main' }} />
                          {foldersError}
                        </MenuItem>
                      ) : folders.length === 0 ? (
                        <MenuItem disabled>
                          No knowledge base folders found
                        </MenuItem>
                      ) : (() => {
                        const availableFolders = getAvailableFolders(formData.knowledgeBases[0]?.id);
                        return availableFolders.length === 0 ? (
                          <MenuItem disabled>
                            No available folders (all folders are selected in other knowledge bases)
                          </MenuItem>
                        ) : (
                          availableFolders.map((folder) => (
                            <MenuItem key={folder.id} value={folder.id}>
                              <Box sx={{ flex: 1 }}>
                                <ListItemText 
                                  primary={folder.name}
                                  secondary={renderFolderInfo(folder)}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.875rem',
                                      fontWeight: 500
                                    },
                                    '& .MuiListItemText-secondary': {
                                      fontSize: '0.75rem'
                                    }
                                  }}
                                />
                              </Box>
                            </MenuItem>
                          ))
                        );
                      })()}
                    </Select>
                  </FormControl>
                  <IconButton 
                    size="small" 
                    className={styles.refreshButton}
                    onClick={loadFolders}
                    disabled={loadingFolders}
                  >
                    {loadingFolders ? <CircularProgress size={16} /> : <Refresh />}
                  </IconButton>
                </Box>
                <Typography 
                  variant="caption" 
                  className={styles.createNewLink}
                  onClick={() => window.open('/knowledge-hub?tab=d1', '_blank')}
                >
                  Create New
                  <OpenInNew fontSize="small" />
                </Typography>
                {errors.knowledgeBase && (
                  <Typography
                    variant="caption"
                    color="error"
                    className={styles.errorText}
                  >
                    Please select a folder
                  </Typography>
                )}

                {/* RAG Configuration Fields */}
                <Box className={styles.section}>
                  <Typography variant="body2" className={styles.sectionLabel}>
                    Number of Chunks <span className={styles.required}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={formData.knowledgeBases[0]?.numberOfChunks || 15}
                    onChange={(e) => {
                      const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                      handleKnowledgeBaseChange(kb.id, "numberOfChunks", parseInt(e.target.value) || 0);
                      if (formData.knowledgeBases.length === 0) {
                        setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, numberOfChunks: parseInt(e.target.value) || 0 }] }));
                      }
                    }}
                    size="small"
                    fullWidth
                    className={styles.textField}
                    error={errors.numberOfChunks}
                    helperText={
                      errors.numberOfChunks
                        ? "Number of chunks must be greater than 0"
                        : ""
                    }
                  />
                </Box>

                <Box className={styles.section}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" className={styles.sectionLabel}>
                      Retrieval Type <span className={styles.required}>*</span>
                    </Typography>
                    <InfoTooltip
                      title="Retrieval Type Guide"
                      maxWidth={1500}
                      placement="right"
                      content={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                            Choose how your knowledge base will find and return relevant information:
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                            ✅ Basic Retrieval:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Finds the most similar documents to your question
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "How to reset password?" → Returns documents about password reset steps
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Simple questions, works with any search type
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#8b5cf6", fontWeight: 600 }}>
                            🔀 Hybrid:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Combines meaning-based search with keyword matching
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "Status of order INV-2041?" → Also matches the exact code, not just similar wording
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Names, codes and exact terms, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                            🎯 MMR (Maximal Marginal Relevance):
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Finds relevant documents but avoids duplicates
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "Benefits of exercise?" → Returns different aspects (health, mental, physical) instead of 3 similar health articles
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Research, comprehensive answers, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                            🚀 HyDE (Hypothetical Document Embeddings):
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Creates a "perfect answer" first, then finds similar documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "What causes headaches?" → AI imagines ideal answer about headache causes, then finds documents matching that ideal answer
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Complex questions, better accuracy, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                            ⚠️ Important:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Hybrid, MMR and HyDE only work with Vector or Hybrid search
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                            • Basic works with all search types (Vector, Hybrid, Keyword)
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ color: "#e36250" }}>
                        <HelpOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                  
                  <FormControl
                    fullWidth
                    size="small"
                    className={`${styles.selectField} ${styles.retrievalSelect}`}
                    error={errors.compatibility}
                  >
                    <InputLabel>Select Retrieval Type...</InputLabel>
                    <Select
                      value={formData.knowledgeBases[0]?.retrievalType || "Basic"}
                      onChange={(e) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "retrievalType", e.target.value);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, retrievalType: e.target.value }] }));
                        }
                        
                        // Check compatibility immediately
                        const compatibility = checkCompatibility({ ...kb, retrievalType: e.target.value });
                        if (!compatibility.compatible) {
                          setErrors(prev => ({ ...prev, compatibility: true }));
                        } else {
                          setErrors(prev => ({ ...prev, compatibility: false }));
                        }
                      }}
                      label="Select Retrieval Type..."
                    >
                      {renderRetrievalTypeOptions()}
                    </Select>
                  </FormControl>
                  
                  {/* Compatibility Status */}
                  {formData.knowledgeBases[0]?.knowledgeBase && formData.knowledgeBases[0].knowledgeBase.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {(() => {
                        const compatibility = checkCompatibility(formData.knowledgeBases[0]);
                        return (
                          <Alert
                            severity={compatibility.compatible ? "success" : "error"}
                            sx={{ 
                              fontSize: "0.75rem",
                              "& .MuiAlert-message": { fontSize: "0.75rem" }
                            }}
                            icon={compatibility.compatible ? <CheckCircle /> : <Warning />}
                          >
                            {compatibility.message}
                          </Alert>
                        );
                      })()}
                    </Box>
                  )}
                </Box>

                <KnowledgeBaseRecencyFields
                  kb={formData.knowledgeBases[0]}
                  onChange={(patch) => handleRecencyChange(formData.knowledgeBases[0]?.id, patch)}
                  styles={styles}
                  InfoTooltip={InfoTooltip}
                />

                <Box className={styles.section}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" className={styles.sectionLabel}>
                      Score Threshold: {formData.knowledgeBases[0]?.scoreThreshold ?? 0}{" "}
                      <span className={styles.required}>*</span>
                    </Typography>
                    <InfoTooltip
                      title="Score Threshold Guide"
                      maxWidth={800}
                      placement="right"
                      content={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                            <strong>What it does:</strong> Filters out documents that are not similar enough to your question
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                            📊 How it works:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.0 = Show all documents (even if not very relevant)
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.5 = Show only moderately relevant documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.8 = Show only highly relevant documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            • 1.0 = Show only perfectly matching documents
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                            💡 Examples:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • <strong>Low (0.0-0.3):</strong> "Tell me about cars" → Returns all car-related content, even loosely related
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • <strong>Medium (0.4-0.6):</strong> "How to change oil?" → Returns car maintenance guides, filters out general car info
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            • <strong>High (0.7-1.0):</strong> "BMW oil change procedure" → Returns only specific BMW oil change instructions
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                            ⚠️ Recommendations:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Start with 0.0-0.3 for general questions
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Use 0.4-0.6 for specific technical questions
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                            • Use 0.7+ only when you need very precise answers
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ color: "#e36250" }}>
                        <HelpOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                  <Box className={styles.sliderContainer}>
                    <Slider
                      value={formData.knowledgeBases[0]?.scoreThreshold || 0.0}
                      onChange={(e, value) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "scoreThreshold", value);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, scoreThreshold: value }] }));
                        }
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      valueLabelDisplay="auto"
                      className={styles.slider}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className={styles.section}>
                <Button
                  variant="contained"
                  onClick={handleAddKnowledgeBase}
                  className={styles.addKnowledgeBaseButton}
                  startIcon={<OpenInNew />}
                >
                  Add Knowledgebase
                </Button>

                {formData.knowledgeBases.map((kb, index) => {
                  const isCollapsed = collapsedKBs.has(kb.id);
                  const hasKnowledgeBaseError = Array.isArray(kb.knowledgeBase) ? kb.knowledgeBase.length === 0 : (!kb.knowledgeBase || kb.knowledgeBase.trim() === "");
                  const hasChunksError = kb.numberOfChunks <= 0;
                  
                  return (
                    <Box key={kb.id} className={styles.knowledgeBaseItem}>
                      <Box className={styles.kbItemHeader}>
                        <Typography variant="body2" className={styles.kbItemTitle}>
                          Knowledge Base {index + 1}
                        </Typography>
                        <Box className={styles.kbItemActions}>
                          <IconButton 
                            size="small" 
                            className={styles.expandButton}
                            onClick={() => toggleCollapse(kb.id)}
                          >
                            {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                          </IconButton>
                          <IconButton 
                            size="small" 
                            className={styles.removeButton}
                            onClick={() => handleRemoveKnowledgeBase(kb.id)}
                          >
                            <Remove />
                          </IconButton>
                        </Box>
                      </Box>

                      {!isCollapsed && (
                        <Box className={styles.kbItemContent}>
                          <Box className={styles.section}>
                            <Typography variant="body2" className={styles.sectionLabel}>
                              Knowledge Base <span className={styles.required}>*</span>
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              className={styles.selectField}
                              error={hasKnowledgeBaseError}
                            >
                              <InputLabel>Select Folder...</InputLabel>
                              <Select
                                value={Array.isArray(kb.knowledgeBase) && kb.knowledgeBase.length > 0 ? findFolderById(kb.knowledgeBase[0])?.id ?? "" : ""}
                                onChange={(e) =>
                                  handleKnowledgeBaseChange(kb.id, "knowledgeBase", [e.target.value])
                                }
                                label="Select Folder..."
                                renderValue={(selected) => {
                                  if (!selected) return "";
                                  const folder = findFolderById(selected);
                                  return folder ? folder.name : "";
                                }}
                                disabled={loadingFolders}
                              >
                                {loadingFolders ? (
                                  <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading folders...
                                  </MenuItem>
                                ) : foldersError ? (
                                  <MenuItem disabled>
                                    <Cancel sx={{ mr: 1, color: 'error.main' }} />
                                    {foldersError}
                                  </MenuItem>
                                ) : folders.length === 0 ? (
                                  <MenuItem disabled>
                                    No knowledge base folders found
                                  </MenuItem>
                                ) : (() => {
                                  const availableFolders = getAvailableFolders(kb.id);
                                  return availableFolders.length === 0 ? (
                                    <MenuItem disabled>
                                      No available folders (all folders are selected in other knowledge bases)
                                    </MenuItem>
                                  ) : (
                                    availableFolders.map((folder) => (
                                      <MenuItem key={folder.id} value={folder.id}>
                                        <Box sx={{ flex: 1 }}>
                                          <ListItemText 
                                            primary={folder.name}
                                            secondary={renderFolderInfo(folder)}
                                            sx={{
                                              '& .MuiListItemText-primary': {
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                              },
                                              '& .MuiListItemText-secondary': {
                                                fontSize: '0.75rem'
                                              }
                                            }}
                                          />
                                        </Box>
                                      </MenuItem>
                                    ))
                                  );
                                })()}
                              </Select>
                            </FormControl>
                            {hasKnowledgeBaseError && (
                              <Typography
                                variant="caption"
                                color="error"
                                className={styles.errorText}
                              >
                                Please select a folder
                              </Typography>
                            )}
                          </Box>

                          <Box className={styles.section}>
                            <Typography variant="body2" className={styles.sectionLabel}>
                              Number of Chunks <span className={styles.required}>*</span>
                            </Typography>
                            <TextField
                              type="number"
                              value={kb.numberOfChunks}
                              onChange={(e) =>
                                handleKnowledgeBaseChange(kb.id, "numberOfChunks", parseInt(e.target.value) || 0)
                              }
                              size="small"
                              fullWidth
                              className={styles.textField}
                              error={hasChunksError}
                              helperText={
                                hasChunksError
                                  ? "Number of chunks must be greater than 0"
                                  : ""
                              }
                            />
                          </Box>

                          <Box className={styles.section}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" className={styles.sectionLabel}>
                                Retrieval Type <span className={styles.required}>*</span>
                              </Typography>
                              <InfoTooltip
                                title="Retrieval Type Guide"
                                maxWidth={1500}
                                placement="right"
                                content={
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                                      Choose how your knowledge base will find and return relevant information:
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      ✅ Basic Retrieval:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Finds the most similar documents to your question
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "How to reset password?" → Returns documents about password reset steps
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Simple questions, works with any search type
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#8b5cf6", fontWeight: 600 }}>
                                      🔀 Hybrid:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Combines meaning-based search with keyword matching
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "Status of order INV-2041?" → Also matches the exact code, not just similar wording
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Names, codes and exact terms, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                                      🎯 MMR (Maximal Marginal Relevance):
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Finds relevant documents but avoids duplicates
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "Benefits of exercise?" → Returns different aspects (health, mental, physical) instead of 3 similar health articles
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Research, comprehensive answers, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      🚀 HyDE (Hypothetical Document Embeddings):
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Creates a "perfect answer" first, then finds similar documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "What causes headaches?" → AI imagines ideal answer about headache causes, then finds documents matching that ideal answer
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Complex questions, better accuracy, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                                      ⚠️ Important:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Hybrid, MMR and HyDE only work with Vector or Hybrid search
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Basic works with all search types (Vector, Hybrid, Keyword)
                                    </Typography>
                                  </Box>
                                }
                              >
                                <IconButton size="small" sx={{ color: "#e36250" }}>
                                  <HelpOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InfoTooltip>
                            </Box>
                            
                            <FormControl
                              fullWidth
                              size="small"
                              className={`${styles.selectField} ${styles.retrievalSelect}`}
                              error={errors.compatibility}
                            >
                              <InputLabel>Select Retrieval Type...</InputLabel>
                              <Select
                                value={kb.retrievalType}
                                onChange={(e) =>
                                  handleKnowledgeBaseChange(kb.id, "retrievalType", e.target.value)
                                }
                                label="Select Retrieval Type..."
                              >
                                {renderRetrievalTypeOptions()}
                              </Select>
                            </FormControl>
                            
                            {/* Compatibility Status */}
                            {kb.knowledgeBase && kb.knowledgeBase.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {(() => {
                                  const compatibility = checkCompatibility(kb);
                                  return (
                                    <Alert
                                      severity={compatibility.compatible ? "success" : "error"}
                                      sx={{ 
                                        fontSize: "0.75rem",
                                        "& .MuiAlert-message": { fontSize: "0.75rem" }
                                      }}
                                      icon={compatibility.compatible ? <CheckCircle /> : <Warning />}
                                    >
                                      {compatibility.message}
                                    </Alert>
                                  );
                                })()}
                              </Box>
                            )}
                          </Box>

                          <KnowledgeBaseRecencyFields
                            kb={kb}
                            onChange={(patch) => handleRecencyChange(kb.id, patch)}
                            styles={styles}
                            InfoTooltip={InfoTooltip}
                          />

                          <Box className={styles.section}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" className={styles.sectionLabel}>
                                Score Threshold: {kb.scoreThreshold}{" "}
                                <span className={styles.required}>*</span>
                              </Typography>
                              <InfoTooltip
                                title="Score Threshold Guide"
                                maxWidth={800}
                                placement="right"
                                content={
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                                      <strong>What it does:</strong> Filters out documents that are not similar enough to your question
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      📊 How it works:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.0 = Show all documents (even if not very relevant)
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.5 = Show only moderately relevant documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.8 = Show only highly relevant documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      • 1.0 = Show only perfectly matching documents
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                                      💡 Examples:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • <strong>Low (0.0-0.3):</strong> "Tell me about cars" → Returns all car-related content, even loosely related
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • <strong>Medium (0.4-0.6):</strong> "How to change oil?" → Returns car maintenance guides, filters out general car info
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      • <strong>High (0.7-1.0):</strong> "BMW oil change procedure" → Returns only specific BMW oil change instructions
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      ⚠️ Recommendations:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Start with 0.0-0.3 for general questions
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Use 0.4-0.6 for specific technical questions
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Use 0.7+ only when you need very precise answers
                                    </Typography>
                                  </Box>
                                }
                              >
                                <IconButton size="small" sx={{ color: "#e36250" }}>
                                  <HelpOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InfoTooltip>
                            </Box>
                            <Box className={styles.sliderContainer}>
                              <Slider
                                value={kb.scoreThreshold}
                                onChange={(e, value) =>
                                  handleKnowledgeBaseChange(kb.id, "scoreThreshold", value)
                                }
                                min={0}
                                max={1}
                                step={0.1}
                                valueLabelDisplay="auto"
                                className={styles.slider}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleCancel}
            variant="text"
            className={styles.cancelButton}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              variant="contained"
              className={styles.saveButton}
              disabled={errors.compatibility}
            >
              {errors.compatibility ? "Fix Compatibility Issues" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default KnowledgeBaseDialog;
